import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { cloneElement, forwardRef, isValidElement } from 'react';
import Button from '../Button';
import TextField from '../TextField';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { controlStyles } from '../styles/controls';
import { colors } from '../theme/colors.stylex';
import { controlMetrics, controlTypography } from '../theme/controls.stylex';
import type { InputGroupProps } from './InputGroup.types';

const styles = stylex.create({
  group: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
    borderRadius: controlMetrics.radius,
    outlineStyle: 'none',
  },
  field: {
    display: 'flex',
    flex: '1 1 0%',
    cursor: 'pointer',
    paddingLeft: controlMetrics.paddingInline,
    paddingRight: controlMetrics.paddingInline,
    paddingTop: controlMetrics.paddingBlock,
    paddingBottom: controlMetrics.paddingBlock,
    lineHeight: controlTypography.lineHeight,
    backgroundColor: stylex.firstThatWorks<string>(colors.fieldBackground, 'inherit'),
    borderRadius: controlMetrics.radius,
    outlineStyle: 'none',
    zIndex: { 'default': 'auto', ':active': 50 },
  },
  left: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
  right: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
  input: { display: 'flex', width: '100%', flex: '1 1 0%', zIndex: { 'default': 'auto', ':focus': 30 } },
  appended: {
    marginLeft: 0,
    borderRadius: 0,
    borderTopRightRadius: { 'default': 0, ':last-child': controlMetrics.radius },
    borderBottomRightRadius: { 'default': 0, ':last-child': controlMetrics.radius },
  },
  overlap: { marginLeft: '-1px' },
  button: {
    zIndex: { 'default': 'auto', ':focus': 40 },
    borderWidth: { 'default': controlMetrics.borderWidth, ':focus': controlMetrics.borderWidth },
  },
  element: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: { 'default': 'auto', ':active': 30 },
  },
});

/**
 * InputGroup.
 */
export const InputGroup = forwardRef<HTMLInputElement, InputGroupProps>((props, ref) => {
  const {
    readonly = false,
    onChange,
    onKeyUp,
    onFocus,
    onBlur,
    onKeyDown,
    name,
    value,
    placeholder,
    append = [],
    extraClassNames = '',
    xstyle,
    containerXstyle,
    id,
    testId,
    showError,
    required,
    autoComplete,
    autoFocus,
    disabled,
    type,
    step,
    min,
    max,
    field,
    maxLength,
    minLength,
    inputGroup,
    ...tooltipProps
  } = props;
  const tooltipData = getQTipData(tooltipProps);

  const groupProps = stylex.props(styles.group, containerXstyle);
  const elementsToAppend = append.filter(Boolean);
  const effectiveInputGroup = inputGroup ?? (elementsToAppend.length > 0 ? 'left' : undefined);
  const fieldProps = stylex.props(
    controlStyles.base,
    styles.field,
    effectiveInputGroup && styles[effectiveInputGroup],
    showError && controlStyles.invalid,
    xstyle,
  );

  // Only pass inputGroup to React components (not DOM elements like div, span, etc.)
  const isReactComponent = isValidElement(field) && typeof field.type !== 'string';
  const fieldWithInputGroup = isReactComponent
    ? cloneElement(field as React.ReactElement<{ inputGroup?: 'left' | 'right' }>, {
        inputGroup: effectiveInputGroup,
      })
    : field;

  return (
    <div id={`input-group-${id}`} {...groupProps} className={clsx(groupProps.className, extraClassNames)}>
      {field ? (
        <div
          data-testid={testId}
          aria-invalid={showError || undefined}
          {...fieldProps}
          className={clsx(fieldProps.className, extraClassNames)}>
          {fieldWithInputGroup}
        </div>
      ) : (
        <TextField
          testId={testId}
          readonly={readonly}
          onChange={onChange}
          onKeyUp={onKeyUp}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          name={name}
          ref={ref}
          showError={showError}
          value={value}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          id={id}
          type={type}
          disabled={disabled}
          step={step}
          min={min}
          max={max}
          maxLength={maxLength}
          minLength={minLength}
          inputGroup={effectiveInputGroup}
          extraClassNames={extraClassNames}
          xstyle={[styles.input, xstyle]}
          {...tooltipData}
        />
      )}

      {elementsToAppend.map((item, index) => {
        return item?.variant === 'button' ? (
          <Button
            key={index}
            {...item.buttonProps}
            xstyle={[styles.appended, index > 0 && styles.overlap, styles.button, item.buttonProps.xstyle]}
          />
        ) : (
          <div
            key={index}
            {...stylex.props(controlStyles.base, styles.appended, index > 0 && styles.overlap, styles.element)}>
            {item?.element}
          </div>
        );
      })}
    </div>
  );
});
InputGroup.displayName = 'InputGroup';
